import React, { useState, useMemo, FC } from "react";
import clsx from "clsx";
import { useMutation } from "@apollo/client";
import moment from "moment";
import { useTranslation } from "lib/i18n/use-translation";
import { CardVariant, CardSize, ContentCard } from "components/ui";
import { extractItem } from "components/ui/ContentCard";
import { IconButton, Button } from "components/ui";
import { LearnerGroupSession } from "../../api/types";
import { ReactComponent as ChevronIcon } from "Assets/icon/icon-chevron.svg";
import appInfo from "utility/app-info";
import { LEARNER_REGISTER_FOR_GROUP_SESSION } from "utility/graphQl/mutation";
import { logError } from "utility/Function";
import { trackEvent } from "utility/mixpanel";
import { useCardTracking } from "../../hooks/use-card-tracking";
import { ContentListType } from "../../pages/home/types";
import {
  getAllSessionDates,
  getMaxSessionDate,
  getAllDateStrings,
  getSessionDateStrings,
  getEarliestAvailableIndex,
  filterSessionsByDate,
} from "./dateUtils";

import styles from "./styles.module.scss";

export type DatedListPropsType = {
  items: LearnerGroupSession[];
};

interface TrackedContentCardProps {
  session: LearnerGroupSession;
  listName: string;
  listPosition: number;
}

const MAX_BUTTONS_VISIBLE = 5;

const TrackedContentCard: FC<TrackedContentCardProps> = ({
  session,
  listPosition,
  listName,
}) => {
  const extractedItem = extractItem(session);
  const [register, { loading }] = useMutation(
    LEARNER_REGISTER_FOR_GROUP_SESSION,
    {
      onCompleted: () => {
        setHasRegistered(true);
      },
    }
  );
  const [hasRegistered, setHasRegistered] = useState(false);
  const { t } = useTranslation(["components", "sessions"]);
  const { logRegisterClick } = useCardTracking({
    item: extractedItem,
    listName,
    listPosition,
    showBiometrics: false,
  });

  const handleRegisterClick = async (sessionId: string) => {
    try {
      await register({
        variables: {
          id: sessionId,
          appInfo,
        },
      });
      logRegisterClick();
    } catch (error) {
      logError("Error registering for group session", error);
    }
  };

  return (
    <ContentCard
      key={session.id}
      item={extractedItem}
      variant={CardVariant.CAROUSEL}
      size={CardSize.LARGE}
      className={styles["content-card"]}
      listName={listName}
      listPosition={listPosition}
      actionButtons={[
        {
          text: hasRegistered
            ? t("components:datedList.reserved")
            : t("sessions:detail.reserveSpot"),
          onClick: (e) => handleRegisterClick(session.id),
          loading,
        },
      ]}
    />
  );
};

enum ScrollDirection {
  Left = "left",
  Right = "right",
}

const DatedList: React.FC<DatedListPropsType> = ({ items }) => {
  const { t } = useTranslation(["home", "common"]);
  const allSessionDates = getAllSessionDates(items);
  const today = moment();
  const todayString = today.format("YYYY-MM-DD");
  const maxSessionDate = getMaxSessionDate(allSessionDates, today);
  const allDateStrings = getAllDateStrings(today, maxSessionDate);
  const sessionDateStrings = useMemo(
    () => getSessionDateStrings(items),
    [items]
  );
  const earliestAvailableIndex = useMemo(
    () => getEarliestAvailableIndex(allDateStrings, sessionDateStrings),
    [allDateStrings, sessionDateStrings]
  );
  const [scrollIndex, setScrollIndex] = useState(() =>
    Math.max(0, earliestAvailableIndex)
  );
  const visibleCount = MAX_BUTTONS_VISIBLE;
  const maxScroll = Math.max(0, allDateStrings.length - visibleCount);

  const [selectedDate, setSelectedDate] = useState(
    () => allDateStrings[earliestAvailableIndex] || allDateStrings[0]
  );

  const filteredSessions = filterSessionsByDate(items, selectedDate);

  const handleScroll = (direction: ScrollDirection) => {
    setScrollIndex((prev) =>
      direction === ScrollDirection.Left
        ? Math.max(0, prev - 1)
        : Math.min(maxScroll, prev + 1)
    );
    trackEvent("Live Sessions Scroll Clicked");
  };

  const visibleDates = allDateStrings.slice(
    scrollIndex,
    scrollIndex + visibleCount
  );

  return (
    <div>
      <h2 className={styles.title}>{t("home:quickActions.liveSessions")}</h2>
      <div className={styles.filteredListWrapper}>
        <div className={styles.filterBarWrapper}>
          <IconButton
            onClick={() => handleScroll(ScrollDirection.Left)}
            disabled={scrollIndex === 0}
            className={styles.arrowButton}
            aria-label={t("common:a11y.scrollLeft")}
            icon={
              <ChevronIcon
                className={clsx(styles.leftArrow, styles.arrowButtonIcon)}
                alt="left arrow"
              />
            }
          />
          <div className={styles.filterBar}>
            {visibleDates.map((dateStr) => {
              const isToday = dateStr === todayString;
              const hasSession = sessionDateStrings.includes(dateStr);
              const label = isToday
                ? t("common:action.today")
                : moment(dateStr, "YYYY-MM-DD").format("ddd Do");
              return (
                <Button
                  key={dateStr}
                  variant="enclosed-primary"
                  monochromeColour="white"
                  className={clsx(styles.button, {
                    [styles.active]: selectedDate === dateStr,
                  })}
                  size="small"
                  onClick={() => {
                    if (hasSession) {
                      setSelectedDate(dateStr);
                      trackEvent("Live Sessions Filter Clicked");
                    }
                  }}
                  disabled={!hasSession}
                >
                  {label}
                </Button>
              );
            })}
          </div>
          <IconButton
            onClick={() => handleScroll(ScrollDirection.Right)}
            disabled={
              scrollIndex === maxScroll ||
              allDateStrings.length <= MAX_BUTTONS_VISIBLE
            }
            className={styles.arrowButton}
            aria-label={t("common:a11y.scrollRight")}
            icon={
              <ChevronIcon
                className={styles.arrowButtonIcon}
                alt="right arrow"
              />
            }
          />
        </div>
        <div className={styles["cards-grid"]}>
          {filteredSessions.map((session, index) => (
            <TrackedContentCard
              key={session.id}
              session={session}
              listName={ContentListType.LiveSessions}
              listPosition={index + 1} // Start from 1 for secondary items
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DatedList;
